import React, { useState, useRef } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import html2canvas from 'html2canvas';
import certBg from '../../../assets/images/Cert.png';
import { useTenant } from '../../../hooks/useTenant';
import IntegrationService from '../../../services/integration_service';

/**
 * KioskCertificate
 *
 * Modal fullscreen que exibe o certificado de conquista no formato Instagram Stories (9:16)
 * usando a imagem certNew.png como fundo, com overlay dinâmico do nome do aluno,
 * atividade, insígnia e logo da empresa.
 */
const KioskCertificate = ({ memberName, evaluationDate, tenantLogo, activityBadgeUrl, activityName, memberPhone, onClose }) => {
    const formattedDate = evaluationDate ? moment(evaluationDate).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY");
    const { idTenant, tenantName } = useTenant();
    const certificateRef = useRef(null);
    const [isSending, setIsSending] = useState(false);

    const PRIMARY_COLOR = '#1a2542';

    const formatPhoneForDisplay = (phone) => {
        if (!phone) return "";
        const clean = phone.replace(/\D/g, "");
        const cleanNoCountry = clean.startsWith("55") && clean.length > 10 ? clean.substring(2) : clean;
        if (cleanNoCountry.length === 11) {
            return `(${cleanNoCountry.substring(0, 2)}) ${cleanNoCountry.substring(2, 7)}-${cleanNoCountry.substring(7)}`;
        } else if (cleanNoCountry.length === 10) {
            return `(${cleanNoCountry.substring(0, 2)}) ${cleanNoCountry.substring(2, 6)}-${cleanNoCountry.substring(6)}`;
        }
        return phone;
    };

    const handleSendToWhatsApp = async () => {
        if (!memberPhone) {
            Swal.fire({ icon: 'warning', title: 'WhatsApp não cadastrado', text: 'Não localizamos um número de WhatsApp em seu perfil. Por favor, procure a recepção para atualizar seu cadastro.', confirmButtonText: 'Entendi' });
            return;
        }

        setIsSending(true);
        try {
            const integration = await IntegrationService.getByProvider(idTenant, "evolution");
            if (!integration || !integration.isActive || !integration.config || !integration.config.apiKey) {
                Swal.fire({ icon: 'error', title: 'WhatsApp Indisponível', text: 'O serviço de envio de WhatsApp não está ativo para este estabelecimento no momento.', confirmButtonText: 'Entendi' });
                setIsSending(false);
                return;
            }

            const status = await IntegrationService.checkEvolutionStatus(integration.config);
            if (status.state !== 'open') {
                Swal.fire({ icon: 'error', title: 'WhatsApp Desconectado', text: 'O serviço de WhatsApp está temporariamente desconectado. Por favor, avise a recepção.', confirmButtonText: 'Entendi' });
                setIsSending(false);
                return;
            }

            if (!certificateRef.current) throw new Error("Elemento do certificado não disponível.");

            const canvas = await html2canvas(certificateRef.current, {
                useCORS: true,
                scale: 2,
                backgroundColor: null,
                logging: false
            });

            const base64Image = canvas.toDataURL('image/png');
            const displayTenant = tenantName || "nossa academia";
            const caption = `Parabéns, *${memberName || "Aluno(a)"}*! 🏆\nVocê conquistou o seu *Certificado de Conquista* nas Aulas de Natação!\n\n📅 Data da conquista: ${formattedDate}\n\n📸 *Poste nas redes sociais, marque a ${displayTenant} e ganhe mais 10 XP!* 🌟\n\n_NeoApp - Direcionando a sua evolução!_`;
            const result = await IntegrationService.sendEvolutionMedia(integration.config, memberPhone, base64Image, caption);

            if (result.success) {
                Swal.fire({ icon: 'success', title: 'Certificado Enviado! 🎉', text: `Seu certificado foi enviado com sucesso para o WhatsApp ${formatPhoneForDisplay(memberPhone)}!`, confirmButtonText: 'Oba, obrigado!' });
            } else {
                let errorMessage = 'Não conseguimos enviar o certificado.';
                if (result.reason === 'invalid_phone') errorMessage = `O número ${formatPhoneForDisplay(memberPhone)} não é um WhatsApp válido.`;
                Swal.fire({ icon: 'error', title: 'Falha no Envio', text: errorMessage, confirmButtonText: 'Entendi' });
            }
        } catch (error) {
            console.error("Erro ao processar envio do certificado:", error);
            Swal.fire({ icon: 'error', title: 'Erro no Processamento', text: 'Houve um problema ao gerar ou enviar seu certificado.', confirmButtonText: 'Entendi' });
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 99999,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                animation: 'certFadeIn 0.4s ease-out',
                cursor: 'pointer',
                padding: '16px',
                overflowY: 'auto'
            }}
        >
            {/* ========== CERTIFICADO — certNew.png como fundo ========== */}
            <div
                ref={certificateRef}
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    width: '360px',
                    height: '640px',
                    overflow: 'hidden',
                    flexShrink: 0
                }}
            >
                {/* Imagem de fundo */}
                <img
                    src={certBg}
                    alt="Certificado"
                    style={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                    }}
                />

                {/* Logo da Empresa — Canto Superior Esquerdo */}
                {tenantLogo && (
                    <div style={{
                        position: 'absolute',
                        top: '16px',
                        left: '16px',
                        zIndex: 10,
                        pointerEvents: 'none'
                    }}>
                        <img
                            src={tenantLogo}
                            alt="Logo da Empresa"
                            style={{
                                maxHeight: '36px',
                                maxWidth: '100px',
                                height: 'auto',
                                width: 'auto',
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                    </div>
                )}

                {/* ===== CONTEÚDO CENTRAL (entre o título "CERTIFICADO" e os personagens) ===== */}
                <div style={{
                    position: 'absolute',
                    top: '32%',
                    left: '0',
                    right: '0',
                    bottom: '42%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 30px',
                    pointerEvents: 'none',
                    zIndex: 5
                }}>
                    {/* Ornamento superior */}
                    <svg width="200" height="16" viewBox="0 0 200 16" style={{ marginBottom: '10px', opacity: 0.4 }}>
                        <path d="M 0,8 Q 20,0 40,8 T 80,8 T 120,8 T 160,8 T 200,8" fill="none" stroke="#1352c3" strokeWidth="1.5" />
                        <circle cx="100" cy="8" r="3" fill="#1352c3" />
                        <line x1="0" y1="8" x2="30" y2="8" stroke="#1352c3" strokeWidth="0.8" />
                        <line x1="170" y1="8" x2="200" y2="8" stroke="#1352c3" strokeWidth="0.8" />
                    </svg>

                    {/* Texto fluido: certificamos que... */}
                    <span style={{
                        fontFamily: 'sans-serif',
                        fontSize: '13px',
                        fontWeight: '500',
                        color: 'rgba(26, 37, 66, 0.75)',
                        lineHeight: '1.8',
                        textAlign: 'justify',
                        marginBottom: '12px',
                        maxWidth: '300px'
                    }}>
                        Certificamos que o aluno{' '}
                        <strong style={{ color: PRIMARY_COLOR, fontWeight: '800' }}>{memberName || "Aluno(a)"}</strong>{' '}
                        completou os requisitos mínimos para avançar do nível
                        {activityName
                            ? <> <strong style={{ color: PRIMARY_COLOR, fontWeight: '800' }}>{activityName}</strong></>
                            : ''
                        }.
                    </span>

                    {/* Parabéns */}
                    <span style={{
                        fontFamily: "'Bubblegum Sans', 'Comic Sans MS', cursive",
                        fontSize: '14px',
                        fontWeight: '900',
                        color: '#1352c3',
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                        marginBottom: '10px'
                    }}>
                        Parabéns!
                    </span>

                    {/* Ornamento inferior */}
                    <svg width="200" height="16" viewBox="0 0 200 16" style={{ opacity: 0.4 }}>
                        <path d="M 0,8 Q 20,16 40,8 T 80,8 T 120,8 T 160,8 T 200,8" fill="none" stroke="#1352c3" strokeWidth="1.5" />
                        <circle cx="100" cy="8" r="3" fill="#1352c3" />
                        <line x1="0" y1="8" x2="30" y2="8" stroke="#1352c3" strokeWidth="0.8" />
                        <line x1="170" y1="8" x2="200" y2="8" stroke="#1352c3" strokeWidth="0.8" />
                    </svg>
                </div>

                {/* Data — Canto inferior direito (acima do mascote) */}
                <div style={{
                    position: 'absolute',
                    bottom: '38%',
                    right: '24px',
                    pointerEvents: 'none',
                    zIndex: 10
                }}>
                    <span style={{
                        fontFamily: "'Bubblegum Sans', 'Comic Sans MS', cursive",
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: PRIMARY_COLOR,
                        letterSpacing: '0.5px',
                        opacity: 0.7
                    }}>
                        {formattedDate}
                    </span>
                </div>
            </div>

            {/* Informativo Redes Sociais */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px dashed rgba(255, 255, 255, 0.3)',
                    borderRadius: '16px',
                    padding: '12px 20px',
                    marginTop: '20px',
                    width: '360px',
                    textAlign: 'center',
                    color: '#fff',
                    fontFamily: 'sans-serif',
                    zIndex: 100000,
                    flexShrink: 0
                }}
            >
                <span style={{ fontSize: '15px', fontWeight: '500', lineHeight: '1.4' }}>
                    📸 Poste nas redes sociais, marque a <strong style={{ color: '#f1b44c' }}>{tenantName || "academia"}</strong> e receba mais <strong style={{ color: '#f1b44c' }}>10 XP</strong>! 🌟
                </span>
            </div>

            {/* Botão WhatsApp */}
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    marginTop: '16px',
                    gap: '12px',
                    width: '360px',
                    zIndex: 100000,
                    flexShrink: 0
                }}
            >
                <button
                    onClick={handleSendToWhatsApp}
                    disabled={isSending}
                    style={{
                        padding: '14px 28px',
                        borderRadius: '50px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                        color: '#fff',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        width: '100%',
                        maxWidth: '360px'
                    }}
                >
                    {isSending ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Enviando para o WhatsApp...
                        </>
                    ) : (
                        <>
                            <i className="mdi mdi-whatsapp" style={{ fontSize: '24px' }}></i>
                            Enviar para meu WhatsApp
                        </>
                    )}
                </button>

                <div
                    onClick={onClose}
                    style={{
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: '14px',
                        fontWeight: '500',
                        letterSpacing: '0.5px',
                        cursor: 'pointer',
                        padding: '8px'
                    }}
                >
                    Toque fora para fechar
                </div>
            </div>
        </div>
    );
};

export default KioskCertificate;
